import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, ChevronRight } from 'lucide-react';

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const projects = {
    'agri-drone': {
      title: 'Agricultural Drone Fleet',
      description: 'Revolutionary automated crop monitoring and precision farming solution',
      image: 'https://images.unsplash.com/photo-1586818579084-53512234a949?auto=format&fit=crop&q=80&w=800',
      overview: 'Our agricultural drone fleet represents a breakthrough in precision farming, combining advanced UAV technology with sophisticated monitoring systems to revolutionize crop management.',
      features: [
        'Real-time crop health monitoring',
        'Precision pesticide application',
        'Automated flight patterns',
        'AI-powered crop analysis',
        'Weather-resistant design',
        'Extended flight time'
      ],
      specifications: [
        { label: 'Flight Time', value: 'Up to 45 minutes' },
        { label: 'Coverage Area', value: '100 acres per flight' },
        { label: 'Camera Resolution', value: '48MP RGB + Multispectral' },
        { label: 'Weather Resistance', value: 'IP65 rated' }
      ],
      gallery: [
        'https://images.unsplash.com/photo-1586818579084-53512234a949?auto=format&fit=crop&q=80&w=800',
        'https://images.unsplash.com/photo-1563514227147-6d2ff665a6a0?auto=format&fit=crop&q=80&w=800',
        'https://images.unsplash.com/photo-1583267746897-2cf415887172?auto=format&fit=crop&q=80&w=800'
      ]
    },
    'iot-platform': {
      title: 'Industrial IoT Platform',
      description: 'Next-generation factory monitoring and control system',
      image: 'https://images.unsplash.com/photo-1563770660941-20978e870e26?auto=format&fit=crop&q=80&w=800',
      overview: 'Our Industrial IoT Platform transforms manufacturing facilities into smart factories, providing real-time monitoring, predictive maintenance, and automated control systems.',
      features: [
        'Real-time monitoring dashboard',
        'Predictive maintenance alerts',
        'Energy consumption optimization',
        'Remote control capabilities',
        'Custom automation workflows',
        'Advanced analytics'
      ],
      specifications: [
        { label: 'Supported Sensors', value: '1000+ types' },
        { label: 'Data Processing', value: 'Edge + Cloud hybrid' },
        { label: 'Response Time', value: '<100ms' },
        { label: 'Uptime SLA', value: '99.99%' }
      ],
      gallery: [
        'https://images.unsplash.com/photo-1563770660941-20978e870e26?auto=format&fit=crop&q=80&w=800',
        'https://images.unsplash.com/photo-1581092160562-40aa08e78837?auto=format&fit=crop&q=80&w=800',
        'https://images.unsplash.com/photo-1537432376769-00f5c2f4c8d2?auto=format&fit=crop&q=80&w=800'
      ]
    },
    'semiconductor': {
      title: 'Custom Semiconductor Design',
      description: 'Application-specific integrated circuit solutions',
      image: 'https://images.unsplash.com/photo-1601791074012-d4e0ee30d9a7?auto=format&fit=crop&q=80&w=800',
      overview: 'We specialize in designing and manufacturing custom semiconductor solutions tailored to specific industry needs, from IoT devices to high-performance computing applications.',
      features: [
        'Custom IC design',
        'Low power consumption',
        'High performance',
        'Thermal optimization',
        'Mixed-signal capabilities',
        'Advanced packaging options'
      ],
      specifications: [
        { label: 'Process Node', value: '5nm to 28nm' },
        { label: 'Design Tools', value: 'Industry standard EDA' },
        { label: 'IP Portfolio', value: '1000+ blocks' },
        { label: 'Verification', value: 'ISO 26262 compliant' }
      ],
      gallery: [
        'https://images.unsplash.com/photo-1601791074012-d4e0ee30d9a7?auto=format&fit=crop&q=80&w=800',
        'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&q=80&w=800',
        'https://images.unsplash.com/photo-1563770660941-20978e870e26?auto=format&fit=crop&q=80&w=800'
      ]
    }
  };

  const project = projects[id as keyof typeof projects];

  if (!project) {
    navigate('/');
    return null;
  }

  return (
    <div className="bg-black text-white py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Navigation */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-400 hover:text-white mb-8"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Projects
        </button>

        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16"
        >
          <div>
            <h1 className="text-4xl font-bold mb-4">{project.title}</h1>
            <p className="text-xl text-gray-400 mb-6">{project.overview}</p>
            <button
              onClick={() => navigate('/contact')}
              className="inline-flex items-center bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 rounded-full font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300"
            >
              Request Demo
              <ChevronRight className="ml-2 h-5 w-5" />
            </button>
          </div>
          <div className="rounded-xl overflow-hidden">
            <img
              src={project.image}
              alt={project.title}
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>

        {/* Features & Specifications */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gray-800 rounded-xl p-8"
          >
            <h2 className="text-2xl font-bold mb-6">Key Features</h2>
            <ul className="space-y-4">
              {project.features.map((feature, index) => (
                <li key={index} className="flex items-center">
                  <Check className="h-5 w-5 text-green-500 mr-3" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gray-800 rounded-xl p-8"
          >
            <h2 className="text-2xl font-bold mb-6">Specifications</h2>
            <div className="space-y-4">
              {project.specifications.map((spec, index) => (
                <div key={index} className="flex justify-between">
                  <span className="text-gray-400">{spec.label}</span>
                  <span className="font-semibold">{spec.value}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Gallery */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-16"
        >
          <h2 className="text-2xl font-bold mb-8">Gallery</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {project.gallery.map((image, index) => (
              <div key={index} className="rounded-xl overflow-hidden">
                <img
                  src={image}
                  alt={`${project.title} gallery ${index + 1}`}
                  className="w-full h-64 object-cover transform hover:scale-105 transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProjectDetails;